import { fabric } from "fabric";

export const parseGradientString = (
	gradientString: string,
): { colorStops: Array<{ color: string; offset: number }>; angle: number } | null => {
	// Parse linear-gradient string format from react-best-gradient-color-picker
	// Format: linear-gradient(90deg, rgba(255,0,0,1) 0%, rgba(0,0,255,1) 100%)
	const linearMatch = gradientString.match(/linear-gradient\((\d+)deg,\s*(.+)\)/i);
	if (linearMatch) {
		const angle = parseInt(linearMatch[1]);
		const colorStopsString = linearMatch[2];

		// Extract colors AND offsets (case-insensitive for RGB/RGBA)
		// Matches: rgba(255,0,0,1) 35%, RGB(24, 84, 23) 100%, #ff0000 50%
		const colorRegex = /(rgba?\([^)]+\)|#[0-9a-fA-F]{3,8})\s+(\d+(?:\.\d+)?)%/gi;
		const colorStops: Array<{ color: string; offset: number }> = [];
		let match;

		while ((match = colorRegex.exec(colorStopsString)) !== null) {
			colorStops.push({
				color: match[1],
				offset: parseFloat(match[2]) / 100,
			});
		}

		if (colorStops.length >= 2) {
			return {
				angle,
				colorStops,
			};
		}
	}
	return null;
};

export const isGradient = (value: string | fabric.Gradient): boolean => {
	if (!value) return false;
	if (typeof value === "object") return true; // fabric.Gradient object
	return typeof value === "string" && value.includes("linear-gradient");
};

export const createGradientString = (
	colorStops: Array<{ color: string; offset: number }>,
	angle: number = 90,
): string => {
	const stops = colorStops.map((stop) => `${stop.color} ${Math.round(stop.offset * 100)}%`).join(", ");
	return `linear-gradient(${angle}deg, ${stops})`;
};

export const fabricGradientToString = (gradient: any): string | null => {
	if (!gradient || typeof gradient !== "object" || !gradient.colorStops) {
		return null;
	}

	const colorStops = gradient.colorStops.map((stop: any) => ({
		color: stop.color,
		offset: stop.offset,
	}));

	if (gradient.coords) {
		const { x1, y1, x2, y2 } = gradient.coords;
		// Calculate angle in radians from direction vector
		// Since we swapped y coords: y1 = 0.5 + 0.5*cos, y2 = 0.5 - 0.5*cos
		// dy = y2 - y1 = -cos(angle), dx = x2 - x1 = sin(angle)
		const dx = x2 - x1;
		const dy = y2 - y1;
		const angleRad = Math.atan2(dx, -dy);
		const angle = (angleRad * (180 / Math.PI) + 360) % 360;
		const normalizedAngle = Math.round(angle);
		return createGradientString(colorStops, normalizedAngle);
	}

	// Default to 90 degrees if no coords
	return createGradientString(colorStops, 90);
};

export const getFillColor = (fill: any): string => {
	// If it's already a string (solid color or gradient string)
	if (typeof fill === "string") {
		return fill;
	}

	// If it's a fabric.Gradient object, convert it to CSS gradient string
	if (fill && typeof fill === "object" && fill.colorStops) {
		const gradientString = fabricGradientToString(fill);
		return gradientString || "#000000";
	}

	return "#000000";
};

export const updateGradientStopColor = (
	gradientString: string,
	newColor: string,
	stopIndex: number = 0,
): string | null => {
	const gradientData = parseGradientString(gradientString);
	if (!gradientData || !gradientData.colorStops || gradientData.colorStops.length === 0) {
		return null;
	}

	// Ensure stopIndex is within bounds
	const safeIndex = Math.max(0, Math.min(stopIndex, gradientData.colorStops.length - 1));

	// Update the color at the specified stop index
	const updatedStops = gradientData.colorStops.map((stop, index) => ({
		...stop,
		color: index === safeIndex ? newColor : stop.color,
	}));

	// Recreate the gradient string with the updated stop
	return createGradientString(updatedStops, gradientData.angle);
};

/**
 * Converts a gradient string to a fabric.Gradient object
 * Handles CSS-like angle conversion (0deg = bottom to top)
 */
export const gradientStringToFabricGradient = (gradientString: string): fabric.Gradient | null => {
	const gradientData = parseGradientString(gradientString);
	if (!gradientData) return null;

	// CSS: 0deg = bottom to top, 90deg = left to right, 180deg = top to bottom
	const rad = (gradientData.angle * Math.PI) / 180;
	const x1 = 0.5 - 0.5 * Math.sin(rad);
	const y1 = 0.5 + 0.5 * Math.cos(rad);
	const x2 = 0.5 + 0.5 * Math.sin(rad);
	const y2 = 0.5 - 0.5 * Math.cos(rad);

	return new fabric.Gradient({
		type: "linear",
		gradientUnits: "percentage",
		coords: { x1, y1, x2, y2 },
		colorStops: gradientData.colorStops.map((stop) => ({
			color: stop.color,
			offset: Math.min(1, Math.max(0, stop.offset)),
		})),
	});
};

/**
 * Processes a fill value, converting gradient strings to fabric.Gradient objects
 */
export const processFillValue = (fill: any): any => {
	if (typeof fill === "string" && isGradient(fill)) {
		return gradientStringToFabricGradient(fill);
	}
	return fill;
};

/**
 * Applies gradients from colorMap to vector sub-objects
 */
export const applyGradientToVectorSubObjects = (element: any, colorMap: Record<string, any>): void => {
	if (!element.objectColors || !colorMap) return;

	Object.keys(colorMap).forEach((oldColor) => {
		const newColor = colorMap[oldColor];
		if (typeof newColor === "string" && isGradient(newColor)) {
			const gradient = gradientStringToFabricGradient(newColor);
			if (gradient && element.objectColors[oldColor]) {
				element.objectColors[oldColor].forEach((obj: any) => {
					obj.set({ fill: gradient, dirty: true });
					obj.setCoords();
				});
			}
		}
	});
};
